import { baseApi } from './baseApi';

const toQuery = (filter = {}) => {
  const p = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const usersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listUsers: build.query({
      query: (filter) => `/users${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      providesTags: (result) => [
        { type: 'User', id: 'LIST' },
        ...(result?.items?.map((u) => ({ type: 'User', id: u.id })) ?? []),
      ],
    }),
    getUser: build.query({
      query: (id) => `/users/${id}`,
      transformResponse: (resp) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'User', id }],
    }),
    // Convenience: ACTIVE doctors only — used for scheduling/calendar so deactivated staff disappear.
    listDoctors: build.query({
      query: () => `/users?role=Doctor&isActive=true&pageSize=200`,
      transformResponse: (resp) => resp.data?.items ?? [],
      providesTags: (result) =>
        (result?.map((u) => ({ type: 'User', id: u.id })) ?? []).concat([{ type: 'User', id: 'DOCTORS' }]),
    }),
    // Convenience: ACTIVE therapists only — used for scheduling/availability.
    listTherapists: build.query({
      query: () => `/users?role=Therapist&isActive=true&pageSize=200`,
      transformResponse: (resp) => resp.data?.items ?? [],
      providesTags: (result) =>
        (result?.map((u) => ({ type: 'User', id: u.id })) ?? []).concat([{ type: 'User', id: 'THERAPISTS' }]),
    }),
    createUser: build.mutation({
      query: (body) => ({ url: '/users', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'DOCTORS' },
        { type: 'User', id: 'THERAPISTS' },
      ],
    }),
    updateUser: build.mutation({
      query: ({ id, ...body }) => ({ url: `/users/${id}`, method: 'PUT', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'DOCTORS' },
        { type: 'User', id: 'THERAPISTS' },
        { type: 'Appointment', id: 'LIST' }, // doctor color may have changed
      ],
    }),
    updateUserRoles: build.mutation({
      query: ({ id, roles }) => ({ url: `/users/${id}/roles`, method: 'PATCH', body: { roles } }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'DOCTORS' },
        { type: 'User', id: 'THERAPISTS' },
      ],
    }),
    deactivateUser: build.mutation({
      query: (id) => ({ url: `/users/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
        { type: 'User', id: 'DOCTORS' },
        { type: 'User', id: 'THERAPISTS' },
      ],
    }),
  }),
});

export const {
  useListUsersQuery,
  useGetUserQuery,
  useListDoctorsQuery,
  useListTherapistsQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateUserRolesMutation,
  useDeactivateUserMutation,
} = usersApi;
